import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useT } from '@hana/i18n';
import './screens.i18n';
import { IconArrowsMaximize, IconChevronsDown, IconChevronsUp, IconMinus, IconPlus, IconSearch, IconX, IconInfoCircle } from '@tabler/icons-react';
import { useToast } from '@hana/ui';
import type { OrgBlock } from './types';
import { SEAT_TONE_CLASS } from './seatTone';
import { SEAT_KIND_TONE, clampZoom, fitZoom, layoutOrgTree, type OtCard } from './orgTree';
import s from './orgtree.module.css';

// Mint-HR · Cơ cấu & ghế · Cây tổ chức (design-specs/Mint-HR-OrgTree.md; code board khối `otr`). Chỉ đọc; bấm ghế báo toast.

const DEFAULT_W = 1240;
const HINT = 'Viền đứt + chấm cam = ghế còn trống · chip = loại giữ ghế (Primary / Concurrent / Acting) · số 4/8 = đang giữ / định biên · bấm Khối / Phòng để thu gọn, bấm ghế để giao ghế · kéo ngang khi cây rộng hơn khung';

const initials = (code: string) => (/^\d/.test(code) ? 'TX' : code.slice(0, 2).toUpperCase());

export function HrOrgTree({ blocks }: { blocks: readonly OrgBlock[] }) {
  const t = useT();
  const toast = useToast();
  const frame = useRef<HTMLDivElement>(null);
  const [vw, setVw] = useState(DEFAULT_W);
  const [vh, setVh] = useState(() => Math.max(420, window.innerHeight - 330));
  const [q, setQ] = useState('');
  const [hit, setHit] = useState(0);
  const [seek, setSeek] = useState(true);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [z, setZ] = useState(1);
  const [px0, setPx] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ x: number; px: number } | null>(null);
  const moved = useRef(false);

  useLayoutEffect(() => {
    const el = frame.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setVw(Math.round(el.clientWidth) || DEFAULT_W));
    ro.observe(el);
    setVw(Math.round(el.clientWidth) || DEFAULT_W);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    const on = () => setVh(Math.max(420, window.innerHeight - 330));
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);

  const lay = useMemo(() => layoutOrgTree(blocks, vw, collapsed, q), [blocks, vw, collapsed, q]);
  const hi = lay.hits.length ? hit % lay.hits.length : -1;
  const sw = lay.width * z;
  const pan = sw > vw;
  let px: number;
  if (!pan) px = (vw - sw) / 2;
  else {
    px = px0 ?? 0;
    if (hi >= 0 && seek) { const h = lay.hits[hi]!; px = vw / 2 - (h.x + h.w / 2) * z; }
    px = Math.min(0, Math.max(vw - sw, px));
  }

  const toggle = (key: string, kids: number, open: boolean) => {
    if (!kids) return;
    const n = new Set(collapsed);
    if (open) n.add(key); else n.delete(key);
    setCollapsed(n);
    setSeek(false);
  };
  const zoom = (v: number) => { setZ(clampZoom(v)); setPx(null); };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { setHit(hit + 1); setSeek(true); }
    if (e.key === 'Escape') { setQ(''); setHit(0); }
  };
  const down = (e: PointerEvent) => { moved.current = false; if (pan) drag.current = { x: e.clientX, px }; };
  const move = (e: PointerEvent) => {
    const g = drag.current;
    if (!g) return;
    const dx = e.clientX - g.x;
    if (Math.abs(dx) > 4) moved.current = true;
    if (moved.current) { setPx(g.px + dx); setSeek(false); setDragging(true); }
  };
  const up = () => { drag.current = null; setDragging(false); };
  const openSeat = (c: OtCard) => () => {
    if (moved.current) { moved.current = false; return; }
    toast({ msg: `Giao ghế ${c.seat.code} · chưa mở ghi ở bản này` });
  };

  return (
    <section className={s.wrap} aria-label="Cây tổ chức" ref={frame}>
      <div className={s.bar}>
        <label className={s.search}>
          <IconSearch size={16} stroke={2} aria-hidden="true" />
          <input aria-label="Tìm người hoặc ghế trên cây" title="Tên, username hoặc mã ghế · Enter: kết quả tiếp · Esc: xoá" placeholder="Tìm người / ghế" value={q} onChange={(e) => { setQ(e.target.value); setHit(0); setSeek(true); }} onKeyDown={onKey} />
          <span aria-live="polite" className={q && !lay.hits.length ? s.hitErr : s.hit}>{q ? (lay.hits.length ? `${hi + 1}/${lay.hits.length}` : '0') : ''}</span>
          {q && (
            <button type="button" aria-label="Xoá tìm" title="Xoá tìm" className={s.clr} onClick={() => { setQ(''); setHit(0); }}>
              <IconX size={14} stroke={2.2} aria-hidden="true" />
            </button>
          )}
        </label>
        <span role="img" aria-label="Chú thích cây tổ chức" title={HINT} className={s.info}><IconInfoCircle size={16} stroke={2} aria-hidden="true" /></span>
        <span className={s.grow} />
        <button type="button" aria-label="Mở hết nhánh" title="Mở hết nhánh" className={s.tool} onClick={() => setCollapsed(new Set())}>
          <IconChevronsDown size={18} stroke={2} aria-hidden="true" />
        </button>
        <button type="button" aria-label="Thu gọn hết (chỉ Khối · Phòng)" title="Thu gọn hết (chỉ Khối · Phòng)" className={s.tool} onClick={() => setCollapsed(new Set(lay.deptKeys))}>
          <IconChevronsUp size={18} stroke={2} aria-hidden="true" />
        </button>
        <div role="group" aria-label="Thu phóng" className={s.zoom}>
          <button type="button" aria-label="Thu nhỏ" title="Thu nhỏ" onClick={() => zoom(z - 0.1)}><IconMinus size={14} stroke={2.4} aria-hidden="true" /></button>
          <button type="button" aria-label="Về 100%" title="Về 100%" className={s.pct} onClick={() => zoom(1)}>{Math.round(z * 100)}%</button>
          <button type="button" aria-label="Phóng to" title="Phóng to" onClick={() => zoom(z + 0.1)}><IconPlus size={14} stroke={2.4} aria-hidden="true" /></button>
        </div>
        <button type="button" className={s.fit} title="Thu cả cây vào một màn" onClick={() => { setZ(fitZoom(vw, vh, lay.width, lay.height)); setPx(null); }}>
          <IconArrowsMaximize size={16} stroke={2} aria-hidden="true" />{t('hr.s.inFrame')}
        </button>
      </div>
      <div
        role="group"
        aria-label="Sơ đồ cây tổ chức"
        className={`${s.canvas} ${pan ? (dragging ? s.grabbing : s.grab) : ''}`}
        style={{ height: Math.ceil(lay.height * z) }}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerLeave={up}
        onPointerCancel={up}
      >
        <div className={`${s.stage} ${dragging ? s.still : ''}`} style={{ left: Math.round(px), width: lay.width, height: lay.height, transform: `scale(${z})` }}>
          <svg aria-hidden="true" width={lay.width} height={lay.height} className={s.links}>
            {lay.links.map((l, i) => <path key={i} d={l.d} style={{ stroke: l.color ?? 'var(--bd)' }} />)}
          </svg>
          {lay.panels.map((p, i) => <div key={i} aria-hidden="true" className={s.panel} style={{ left: p.x, top: p.y, width: p.w, height: p.h, ['--c' as string]: p.color }} />)}
          <div aria-hidden="true" className={s.rootBox} style={{ left: lay.rootBox.x, top: lay.rootBox.y, width: lay.rootBox.w, height: lay.rootBox.h }} />
          {lay.nodes.map((n) => (
            <button
              key={n.key}
              type="button"
              aria-expanded={n.open}
              aria-label={`${n.label}, ${n.seats} ghế, ${n.vacant} trống`}
              title={`${n.label} · Định biên ${n.seats} · đang giữ ${n.seats - n.vacant} · trống ${n.vacant}${n.kids ? ` · bấm để ${n.open ? 'thu gọn' : 'mở'} nhánh` : ''}`}
              className={`${s.node} ${n.root ? s.nodeRoot : ''}`}
              style={{ left: n.x, top: n.y, width: n.w, height: n.h, ['--c' as string]: n.color }}
              onClick={() => toggle(n.key, n.kids, n.open)}
            >
              <b className={n.root || n.label.startsWith('Khối') ? s.nodeB : s.nodeD}>{n.label}</b>
              <span>{n.seats} ghế · <em className={n.vacant ? s.warn : undefined}>{n.vacant} trống</em></span>
              {n.kids > 0 && (
                <span aria-hidden="true" className={s.tg}>{n.open ? <IconMinus size={12} stroke={2.6} /> : <IconPlus size={12} stroke={2.6} />}</span>
              )}
            </button>
          ))}
          {lay.cards.map((c, i) => {
            const empty = !c.holders.length;
            const h0 = c.holders[0];
            const isCur = lay.hits[hi] === c;
            const tone = h0 ? SEAT_KIND_TONE[h0.kind] : null;
            const who = h0 ? `${h0.code}${c.holders.length > 1 ? ` +${c.holders.length - 1}` : ''}` : 'Trống';
            const hold = c.holders.map((h) => `${h.code} ${h.kind}`).join(', ');
            return (
              <button
                key={`${c.seat.code}-${i}`}
                type="button"
                data-ot-cur={isCur ? '1' : '0'}
                aria-label={`Ghế ${c.seat.code} ${c.seat.name}${empty ? ', trống' : `, ${hold}`}. Giao ghế`}
                title={`${c.seat.code} · ${c.seat.name} · ${empty ? 'chưa có người' : c.holders.map((h) => `${h.code} (${h.kind})`).join(', ')}${c.vacant ? ` · trống ${c.vacant}/${c.seat.plan}` : ''} · bấm để giao ghế`}
                className={`${s.card} ${empty ? s.cardEmpty : ''} ${c.hit ? (isCur ? s.hitCur : s.hitOn) : ''}`}
                style={{ left: c.x, top: c.y, width: c.w, height: c.h, opacity: q && !c.hit ? 0.45 : 1 }}
                onClick={openSeat(c)}
              >
                {empty && <span aria-hidden="true" className={s.plus}><IconPlus size={14} stroke={2.4} /></span>}
                <span aria-hidden="true" className={s.avs}>
                  {c.holders.slice(0, 3).map((h, j) => <span key={j} className={s.av} style={{ marginLeft: j ? -8 : 0 }}>{initials(h.code)}</span>)}
                </span>
                <span className={s.tx}>
                  <span className={s.row1}>
                    <b className={empty ? s.mute : undefined}>{who}</b>
                    {h0 && tone && <span className={`${s.chip} ${SEAT_TONE_CLASS[tone]}`}>{h0.kind}</span>}
                  </span>
                  <span className={s.row2}>
                    <span>{c.seat.name}</span>
                    {c.seat.plan > 1 && <span className={c.vacant ? s.warn : undefined}>{c.fill}/{c.seat.plan}</span>}
                  </span>
                </span>
                {c.vacant > 0 && <span aria-hidden="true" className={s.dot} />}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
